import { useState, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft, ArrowRight, Check, PartyPopper, CalendarDays, MapPin,
  Users, Wallet, LayoutGrid, Send, Sparkles, Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import {
  EVENT_TYPES, LOCATION_OPTIONS, GUEST_OPTIONS, BUDGET_OPTIONS, CATEGORIES,
  PLANNING_PACKAGES, CONTACT_METHODS, computeEstimate, formatMoney,
  getPackagePrice, isScaledBudget,
} from "@/lib/eventData";

const STEP_META = [
  { n: 1, title: "What are you celebrating?", icon: PartyPopper },
  { n: 2, title: "Date & time", icon: CalendarDays },
  { n: 3, title: "Location", icon: MapPin },
  { n: 4, title: "Guest count", icon: Users },
  { n: 5, title: "Budget", icon: Wallet },
  { n: 6, title: "What do you want?", icon: LayoutGrid },
  { n: 7, title: "Your event & contact", icon: Send },
];

const TOTAL = 7;

function Chip({ selected, children, onClick, testid }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      className={`text-left rounded-xl border px-4 py-3.5 font-semibold transition-colors duration-200 ${
        selected
          ? "bg-nick-primary border-nick-primary text-white"
          : "bg-white border-nick-border text-nick-dark hover:border-nick-primary/60"
      }`}
    >
      <span className="flex items-center justify-between gap-2">
        {children}
        {selected && <Check className="h-4 w-4 shrink-0" />}
      </span>
    </button>
  );
}

export default function PlanEvent() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const [data, setData] = useState({
    event_types: [],
    event_date: "", start_time: "", end_time: "",
    flexible_date: false, flexible_time: false, date_unknown: false,
    location_type: "", zip_or_city: "",
    guest_count: "", budget_range: "",
    services: {}, nick_decide: [],
    selected_tier: "",
    planning_package: "", planning_package_price: null,
    needs_budget_help: false, budget_help_notes: "",
    first_name: "", last_name: "", email: "", phone: "", preferred_contact: "",
    notes: "",
  });

  const set = (patch) => setData((d) => ({ ...d, ...patch }));

  const estimate = useMemo(() => computeEstimate(data), [data]);

  const toggleArr = (key, val) =>
    setData((d) => {
      const arr = d[key];
      return { ...d, [key]: arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val] };
    });

  const toggleService = (cat, item) =>
    setData((d) => {
      const cur = d.services[cat] || [];
      const next = cur.includes(item) ? cur.filter((x) => x !== item) : [...cur, item];
      const services = { ...d.services, [cat]: next };
      if (next.length === 0) delete services[cat];
      return { ...d, services };
    });

  const toggleNickDecide = (cat) =>
    setData((d) => ({
      ...d,
      nick_decide: d.nick_decide.includes(cat)
        ? d.nick_decide.filter((x) => x !== cat)
        : [...d.nick_decide, cat],
    }));

  const canNext = () => {
    switch (step) {
      case 1: return data.event_types.length > 0;
      case 2: return data.date_unknown || data.event_date || data.flexible_date;
      case 3: return !!data.location_type;
      case 4: return !!data.guest_count;
      case 5: return !!data.budget_range;
      default: return true;
    }
  };

  const next = () => {
    if (!canNext()) {
      toast.error("Pick an option to continue — or choose \"I don't know yet.\"");
      return;
    }
    setStep((s) => Math.min(TOTAL, s + 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const prev = () => {
    setStep((s) => Math.max(1, s - 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submit = async () => {
    if (!data.first_name || !data.last_name || !data.email || !data.phone) {
      toast.error("Please add your name, email and phone so Nick can reach you.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...data, estimate };
      const { data: res } = await api.post("/events", payload);
      setResult(res);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not send. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // ---------- confirmation ----------
  if (result) {
    return (
      <div className="min-h-screen bg-nick-bg flex items-center justify-center px-5">
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-lg w-full text-center bg-white border border-nick-border rounded-3xl p-10 shadow-sm"
          data-testid="submit-success"
        >
          <div className="mx-auto h-16 w-16 rounded-2xl bg-nick-primary/10 text-nick-primary flex items-center justify-center">
            <PartyPopper className="h-8 w-8" />
          </div>
          <h1 className="mt-6 font-display font-extrabold text-3xl">We got it!</h1>
          <p className="mt-3 text-nick-muted text-lg">
            Nick has your event request. He'll review the details and get back to you.
          </p>
          <div className="mt-6 rounded-2xl bg-nick-bg border border-nick-border p-5">
            <div className="overline text-nick-muted">Your request number</div>
            <div data-testid="event-number" className="mt-1 font-display font-extrabold text-2xl text-nick-primary tracking-wide">
              {result.event_number}
            </div>
          </div>
          <Link to="/">
            <Button className="mt-8 rounded-full bg-nick-dark hover:bg-black text-white font-bold px-8 h-12">
              Back to Home
            </Button>
          </Link>
        </motion.div>
      </div>
    );
  }

  const Meta = STEP_META[step - 1];
  const StepIcon = Meta.icon;

  return (
    <div className="min-h-screen bg-nick-bg">
      {/* top bar */}
      <header className="sticky top-0 z-40 nick-glass border-b border-black/5">
        <div className="mx-auto max-w-3xl px-5 h-16 flex items-center justify-between">
          <Link to="/" className="font-display font-extrabold text-lg tracking-tight">
            Let's Party <span className="text-nick-primary">Houston</span>
          </Link>
          <span data-testid="step-indicator" className="overline text-nick-muted">
            Step {step} of {TOTAL}
          </span>
        </div>
        <div className="h-1.5 bg-nick-border">
          <motion.div
            className="h-full bg-nick-primary"
            animate={{ width: `${(step / TOTAL) * 100}%` }}
            transition={{ duration: 0.4 }}
          />
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-5 py-10 pb-32">
        <div className="flex items-center gap-3 text-nick-primary">
          <StepIcon className="h-6 w-6" />
          <span className="overline">{Meta.title}</span>
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
            className="mt-5"
          >
            {/* STEP 1 */}
            {step === 1 && (
              <div>
                <h1 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight">
                  What are you celebrating?
                </h1>
                <p className="mt-2 text-nick-muted">Pick one or more — whatever fits.</p>
                <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {EVENT_TYPES.map((t) => (
                    <Chip key={t} testid={`event-type-${t}`} selected={data.event_types.includes(t)} onClick={() => toggleArr("event_types", t)}>
                      {t}
                    </Chip>
                  ))}
                </div>
              </div>
            )}

            {/* STEP 2 */}
            {step === 2 && (
              <div>
                <h1 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight">When is it?</h1>
                <p className="mt-2 text-nick-muted">A rough idea is perfectly fine.</p>
                <div className="mt-6 space-y-5">
                  <div>
                    <Label className="font-semibold">Event date</Label>
                    <Input
                      type="date" data-testid="event-date-input" disabled={data.date_unknown}
                      value={data.event_date} onChange={(e) => set({ event_date: e.target.value })}
                      className="mt-2 h-12 rounded-xl"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="font-semibold">Start time</Label>
                      <Input type="time" data-testid="start-time-input" value={data.start_time}
                        onChange={(e) => set({ start_time: e.target.value })} className="mt-2 h-12 rounded-xl" />
                    </div>
                    <div>
                      <Label className="font-semibold">End time</Label>
                      <Input type="time" data-testid="end-time-input" value={data.end_time}
                        onChange={(e) => set({ end_time: e.target.value })} className="mt-2 h-12 rounded-xl" />
                    </div>
                  </div>
                  <div className="space-y-3 pt-1">
                    {[
                      ["flexible_date", "My date is flexible"],
                      ["flexible_time", "My time is flexible"],
                      ["date_unknown", "I don't know yet"],
                    ].map(([k, label]) => (
                      <label key={k} className="flex items-center gap-3 cursor-pointer">
                        <Checkbox data-testid={`check-${k}`} checked={data[k]} onCheckedChange={(v) => set({ [k]: !!v })} />
                        <span className="font-medium">{label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* STEP 3 */}
            {step === 3 && (
              <div>
                <h1 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight">Where will it happen?</h1>
                <p className="mt-2 text-nick-muted">No exact address needed yet.</p>
                <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {LOCATION_OPTIONS.map((o) => (
                    <Chip key={o} testid={`location-${o}`} selected={data.location_type === o} onClick={() => set({ location_type: o })}>
                      {o}
                    </Chip>
                  ))}
                </div>
                <div className="mt-6">
                  <Label className="font-semibold">ZIP code or city</Label>
                  <Input data-testid="zip-city-input" placeholder="e.g. Kingwood or 77339" value={data.zip_or_city}
                    onChange={(e) => set({ zip_or_city: e.target.value })} className="mt-2 h-12 rounded-xl" />
                  <p className="mt-2 text-sm text-nick-muted">
                    Outside Houston? Nick may be able to travel — travel fees may apply.
                  </p>
                </div>
              </div>
            )}

            {/* STEP 4 */}
            {step === 4 && (
              <div>
                <h1 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight">How many guests?</h1>
                <p className="mt-2 text-nick-muted">A ballpark is fine.</p>
                <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {GUEST_OPTIONS.map((o) => (
                    <Chip key={o} testid={`guest-${o}`} selected={data.guest_count === o} onClick={() => set({ guest_count: o })}>
                      {o}
                    </Chip>
                  ))}
                </div>
              </div>
            )}

            {/* STEP 5 */}
            {step === 5 && (
              <div>
                <h1 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight">
                  What's your approximate budget?
                </h1>
                <div className="mt-3 rounded-xl bg-nick-primary/10 text-nick-primary-hover p-4 text-sm font-medium">
                  Not sure what your event should cost? That's okay. Nick can help you build a realistic budget.
                </div>
                <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {BUDGET_OPTIONS.map((o) => (
                    <Chip key={o} testid={`budget-${o}`} selected={data.budget_range === o} onClick={() => set({ budget_range: o })}>
                      {o}
                    </Chip>
                  ))}
                </div>
              </div>
            )}

            {/* STEP 6 */}
            {step === 6 && (
              <div>
                <h1 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight">What do you want?</h1>
                <p className="mt-2 text-nick-muted">Tap anything that sounds good. Not sure? Let Nick decide.</p>
                <div className="mt-6 space-y-4">
                  {CATEGORIES.map((cat) => {
                    const nickDecide = data.nick_decide.includes(cat.key);
                    return (
                      <div key={cat.key} className="rounded-2xl bg-white border border-nick-border p-5">
                        <div className="flex items-center justify-between gap-3 flex-wrap">
                          <h3 className="font-display font-bold text-lg">{cat.key}</h3>
                          <button
                            type="button"
                            data-testid={`nick-decide-${cat.key}`}
                            onClick={() => toggleNickDecide(cat.key)}
                            className={`text-xs font-bold rounded-full px-3 py-1.5 border transition-colors ${
                              nickDecide
                                ? "bg-nick-secondary border-nick-secondary text-white"
                                : "bg-white border-nick-secondary/40 text-nick-secondary hover:bg-nick-secondary/10"
                            }`}
                          >
                            <Sparkles className="inline h-3.5 w-3.5 mr-1" />
                            I'm not sure — let Nick decide
                          </button>
                        </div>
                        {!nickDecide && (
                          <div className="mt-4 flex flex-wrap gap-2">
                            {cat.items.map((item) => {
                              const sel = (data.services[cat.key] || []).includes(item);
                              return (
                                <button
                                  key={item}
                                  type="button"
                                  data-testid={`service-${cat.key}-${item}`}
                                  onClick={() => toggleService(cat.key, item)}
                                  className={`text-sm font-semibold rounded-full px-3.5 py-2 border transition-colors ${
                                    sel
                                      ? "bg-nick-primary border-nick-primary text-white"
                                      : "bg-nick-bg border-nick-border text-nick-dark hover:border-nick-primary/60"
                                  }`}
                                >
                                  {item}
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* STEP 7 */}
            {step === 7 && (
              <div className="space-y-8">
                <div>
                  <h1 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight">Your event</h1>
                  <div className="mt-5 rounded-2xl bg-white border border-nick-border p-6 space-y-3">
                    <SummaryRow label="Celebrating" value={data.event_types.join(", ") || "—"} />
                    <SummaryRow label="Date" value={data.date_unknown ? "I don't know yet" : (data.event_date || (data.flexible_date ? "Flexible" : "—"))} />
                    <SummaryRow label="Location" value={[data.location_type, data.zip_or_city].filter(Boolean).join(" · ") || "—"} />
                    <SummaryRow label="Guests" value={data.guest_count || "—"} />
                    <SummaryRow label="Budget" value={data.budget_range || "—"} />
                    <SummaryRow
                      label="Services"
                      value={
                        [
                          ...Object.values(data.services).flat(),
                          ...data.nick_decide.map((c) => `${c} (Nick decides)`),
                        ].join(", ") || "None yet"
                      }
                    />
                  </div>
                </div>

                {/* estimate */}
                {estimate.has_estimate && (
                  <div className="rounded-2xl bg-nick-dark text-white p-6">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div className="overline text-white/70">Estimated event cost</div>
                      <div className="text-xs text-white/60">Tap the level you want — Nick builds to it.</div>
                    </div>
                    <div className="mt-4 grid grid-cols-3 gap-3 text-center">
                      <EstTier label="Budget" value={estimate.budget} tier="Budget"
                        selected={data.selected_tier === "Budget"} onClick={() => set({ selected_tier: data.selected_tier === "Budget" ? "" : "Budget" })} />
                      <EstTier label="Nick's Pick" value={estimate.recommended} tier="Nick's Pick" highlight
                        selected={data.selected_tier === "Nick's Pick"} onClick={() => set({ selected_tier: data.selected_tier === "Nick's Pick" ? "" : "Nick's Pick" })} />
                      <EstTier label="Premium" value={estimate.premium} tier="Premium"
                        selected={data.selected_tier === "Premium"} onClick={() => set({ selected_tier: data.selected_tier === "Premium" ? "" : "Premium" })} />
                    </div>
                    {data.selected_tier && (
                      <div className="mt-3 text-sm text-white/90">
                        You picked <span className="font-bold text-nick-primary">{data.selected_tier}</span>. Nick will plan around that number.
                      </div>
                    )}
                    <p className="mt-4 text-xs text-white/60">
                      These are estimates only. Vendor availability and final pricing must be confirmed before booking.
                    </p>
                  </div>
                )}

                {/* planning packages */}
                <div>
                  <div className="overline text-nick-primary">Nick's Planning Services</div>
                  <p className="mt-2 text-sm text-nick-muted">
                    Here's exactly what you get from Nick for your planning fee.
                  </p>
                  {isScaledBudget(data.budget_range) && (
                    <p className="mt-1 text-sm text-nick-muted">
                      Smaller party? Smaller fee. These prices are scaled for your <span className="font-semibold">{data.budget_range}</span> budget.
                    </p>
                  )}
                  <div className="mt-4 grid grid-cols-1 gap-3">
                    {PLANNING_PACKAGES.map((p) => {
                      const sel = data.planning_package === p.id;
                      const price = getPackagePrice(p.id, data.budget_range);
                      const scaled = price !== p.price;
                      return (
                        <button
                          key={p.id}
                          type="button"
                          data-testid={`package-${p.id}`}
                          onClick={() => set({ planning_package: sel ? "" : p.id, planning_package_price: sel ? null : price })}
                          className={`text-left rounded-2xl border p-5 transition-colors ${
                            sel ? "bg-nick-primary/5 border-nick-primary" : "bg-white border-nick-border hover:border-nick-primary/50"
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <span className="font-display font-bold text-lg">{p.id}</span>
                            <span className="flex items-baseline gap-2">
                              {scaled && <span className="text-sm text-nick-muted line-through">${p.price}</span>}
                              <span className="font-display font-extrabold text-nick-primary text-xl">${price}</span>
                            </span>
                          </div>
                          <p className="mt-1 text-sm text-nick-muted">{p.tagline}</p>
                          <ul className="mt-3 space-y-1.5">
                            {p.includes.map((inc) => (
                              <li key={inc} className="flex items-start gap-2 text-sm text-nick-dark">
                                <Check className="h-4 w-4 text-nick-primary shrink-0 mt-0.5" />
                                <span>{inc}</span>
                              </li>
                            ))}
                          </ul>
                          {sel && (
                            <span className="mt-3 inline-flex items-center gap-1 text-xs font-bold text-nick-primary">
                              <Check className="h-3.5 w-3.5" /> Selected
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                  <p className="mt-3 text-sm text-nick-muted">
                    Large, complex, wedding, corporate or 200+ guest events may need a custom quote.
                  </p>
                </div>

                {/* community pricing */}
                <div className="rounded-2xl bg-white border border-nick-border p-6">
                  <Label className="font-semibold">Do you need help keeping this event within your budget?</Label>
                  <div className="mt-3 flex gap-3">
                    {[["Yes", true], ["No", false]].map(([label, val]) => (
                      <button
                        key={label}
                        type="button"
                        data-testid={`budget-help-${label}`}
                        onClick={() => set({ needs_budget_help: val })}
                        className={`rounded-full px-6 py-2.5 font-semibold border transition-colors ${
                          data.needs_budget_help === val
                            ? "bg-nick-primary border-nick-primary text-white"
                            : "bg-nick-bg border-nick-border hover:border-nick-primary/60"
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                  {data.needs_budget_help && (
                    <Textarea
                      data-testid="budget-help-notes"
                      placeholder="Tell Nick what you're working with."
                      value={data.budget_help_notes}
                      onChange={(e) => set({ budget_help_notes: e.target.value })}
                      className="mt-4 rounded-xl min-h-[90px]"
                    />
                  )}
                </div>

                {/* contact */}
                <div>
                  <div className="overline text-nick-primary">Your contact info</div>
                  <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Field label="First name *" testid="first-name" value={data.first_name} onChange={(v) => set({ first_name: v })} />
                    <Field label="Last name *" testid="last-name" value={data.last_name} onChange={(v) => set({ last_name: v })} />
                    <Field label="Email *" testid="email" type="email" value={data.email} onChange={(v) => set({ email: v })} />
                    <Field label="Phone *" testid="phone" type="tel" value={data.phone} onChange={(v) => set({ phone: v })} />
                  </div>
                  <div className="mt-4">
                    <Label className="font-semibold">Preferred contact method</Label>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {CONTACT_METHODS.map((m) => (
                        <button
                          key={m}
                          type="button"
                          data-testid={`contact-method-${m}`}
                          onClick={() => set({ preferred_contact: m })}
                          className={`rounded-full px-4 py-2 text-sm font-semibold border transition-colors ${
                            data.preferred_contact === m
                              ? "bg-nick-secondary border-nick-secondary text-white"
                              : "bg-white border-nick-border hover:border-nick-secondary/60"
                          }`}
                        >
                          {m}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="mt-4">
                    <Label className="font-semibold">Tell Nick anything else he should know</Label>
                    <Textarea
                      data-testid="notes-input"
                      value={data.notes}
                      onChange={(e) => set({ notes: e.target.value })}
                      className="mt-2 rounded-xl min-h-[110px]"
                      placeholder="Theme ideas, must-haves, dietary needs, inspiration…"
                    />
                  </div>
                  <p className="mt-4 text-xs text-nick-muted">
                    Prices shown through Let's Party Houston may be estimates until the vendor confirms availability and pricing.
                  </p>
                </div>
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </main>

      {/* sticky footer nav */}
      <div className="fixed bottom-0 inset-x-0 z-40 nick-glass border-t border-black/5">
        <div className="mx-auto max-w-3xl px-5 py-4 flex items-center gap-3">
          {step > 1 ? (
            <Button variant="outline" data-testid="wizard-prev" onClick={prev} className="rounded-full h-12 px-5 font-semibold">
              <ArrowLeft className="h-4 w-4 mr-1" /> Back
            </Button>
          ) : (
            <Link to="/">
              <Button variant="outline" className="rounded-full h-12 px-5 font-semibold">
                <ArrowLeft className="h-4 w-4 mr-1" /> Home
              </Button>
            </Link>
          )}
          <div className="flex-1" />
          {step < TOTAL ? (
            <Button data-testid="wizard-next" onClick={next} className="rounded-full h-12 px-8 font-bold bg-nick-primary hover:bg-nick-primary-hover text-white">
              Continue <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          ) : (
            <Button data-testid="wizard-submit" onClick={submit} disabled={submitting} className="rounded-full h-12 px-8 font-bold bg-nick-primary hover:bg-nick-primary-hover text-white">
              {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
              Send My Event to Nick
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function SummaryRow({ label, value }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <span className="text-sm text-nick-muted w-28 shrink-0">{label}</span>
      <span className="text-sm font-semibold text-right">{value}</span>
    </div>
  );
}

function EstTier({ label, value, selected, onClick }) {
  return (
    <button
      type="button"
      data-testid={`est-tier-${label}`}
      onClick={onClick}
      className={`rounded-xl p-3 border transition-colors text-center ${
        selected
          ? "bg-nick-primary border-nick-primary ring-2 ring-white/60"
          : "bg-white/10 border-white/15 hover:bg-white/20"
      }`}
    >
      <div className="text-[11px] uppercase tracking-wider text-white/80 flex items-center justify-center gap-1">
        {selected && <Check className="h-3 w-3" />}
        {label}
      </div>
      <div className="mt-1 font-display font-extrabold text-xl">{formatMoney(value)}</div>
    </button>
  );
}

function Field({ label, testid, value, onChange, type = "text" }) {
  return (
    <div>
      <Label className="font-semibold">{label}</Label>
      <Input data-testid={`field-${testid}`} type={type} value={value}
        onChange={(e) => onChange(e.target.value)} className="mt-2 h-12 rounded-xl" />
    </div>
  );
}
